import { CheckIcon } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { WizardStep } from '../types';

type StepPillProps = {
  active?: boolean;
  done?: boolean;
  label: string;
  number: WizardStep;
};

const StepPill: FC<StepPillProps> = ({ active, done, label, number }) => (
  <div
    aria-current={active ? 'step' : undefined}
    className={cn(
      'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold',
      active || done
        ? 'border-primary-light bg-primary-light text-white'
        : 'border-slate-200 bg-slate-100 text-slate-500'
    )}
  >
    <span
      className={cn(
        'flex size-5 items-center justify-center rounded-full text-xs',
        active || done ? 'bg-white/25 text-white' : 'bg-slate-300 text-slate-600'
      )}
    >
      {done ? <CheckIcon className="size-3" /> : number}
    </span>

    {label}
  </div>
);

type StepIndicatorProps = {
  step: WizardStep;
};

export const StepIndicator: FC<StepIndicatorProps> = ({ step }) => {
  const { t } = useTranslation();
  const steps: Array<{ number: WizardStep; label: string }> = [
    { number: 1, label: t('selectEstate') },
    { number: 2, label: t('upload') },
    { number: 3, label: t('finish') },
  ];

  return (
    <div className="flex items-center justify-center px-6 py-5">
      {steps.map((stepItem, index) => (
        <div key={stepItem.number} className="flex items-center">
          <StepPill
            number={stepItem.number}
            label={stepItem.label}
            active={step === stepItem.number}
            done={step > stepItem.number}
          />

          {index < steps.length - 1 && <div className="mx-2 h-px w-6 bg-slate-200" />}
        </div>
      ))}
    </div>
  );
};
